/* ============================================================================
 * plan.js - plan config + account/session state for the Pro paywall
 * ----------------------------------------------------------------------------
 * Accounts live in the Cloudflare Worker (paywall/worker.mjs). The browser
 * keeps a signed session token + the last /api/me snapshot in localStorage so
 * pages render instantly, then re-syncs in the background.
 *
 * Config: VITE_PAYWALL_API (worker URL) and VITE_STRIPE_PAYMENT_LINK.
 * ==========================================================================*/
import { useEffect, useState } from "react";

export const PRO_PRICE_LABEL = "$9.99";
export const PRO_PERIOD = "/month";
export const FREE_HISTORY_MONTHS = 12;
export const FREE_WATCHLIST_MAX = 5;

export const PAYMENT_LINK = import.meta.env?.VITE_STRIPE_PAYMENT_LINK || "";
export const API = import.meta.env?.VITE_PAYWALL_API || "";
export const ACCOUNTS_ENABLED = !!API;

const LS = "tbs-session";
const EVT = "tbs-account";
const LEGACY_WATCH = "meridian.watchlist.v1";

let session = (() => { try { return JSON.parse(localStorage.getItem(LS) || "null"); } catch { return null; } })();

function commit(next) {
  session = next;
  try { next ? localStorage.setItem(LS, JSON.stringify(next)) : localStorage.removeItem(LS); } catch {}
  window.dispatchEvent(new Event(EVT));
}

export function getAccount() { return session?.me || null; }
export function getPlan() { return session?.me?.plan === "pro" ? "pro" : "free"; }
export function isPro() { return getPlan() === "pro"; }
export function getWatchlist() { return new Set(session?.me?.watchlist || []); }

/* ---------------------------------------------------------------- fetch */
export async function api(path, { method = "GET", body } = {}) {
  if (!API) throw new Error("Accounts are not configured on this deployment");
  const headers = { "Content-Type": "application/json" };
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  const res = await fetch(`${API}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    if (res.status === 401 && session) commit(null);   // stale or revoked session
    if (res.status === 404 && data?.error === "not found")   // worker not yet updated for accounts
      throw new Error("Accounts are being enabled on the server. Please try again in a few minutes.");
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data;
}

/* ----------------------------------------------------------- auth flows */
async function afterAuth(d) {
  commit({ token: d.token, me: d.me });
  /* Bring a pre-accounts device watchlist along, once. */
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_WATCH) || "[]");
    if (Array.isArray(legacy) && legacy.length && !(d.me.watchlist || []).length) {
      const max = d.me.plan === "pro" ? Infinity : FREE_WATCHLIST_MAX;
      await setWatchlist(legacy.slice(0, max));
    }
    localStorage.removeItem(LEGACY_WATCH);
  } catch {}
  return session.me;
}
export const signUp = (email, password) => api("/api/signup", { method: "POST", body: { email, password } }).then(afterAuth);
export const signIn = (email, password) => api("/api/signin", { method: "POST", body: { email, password } }).then(afterAuth);
export function signOut() { commit(null); }

export async function refreshMe() {
  if (!session?.token) return null;
  try {
    const me = await api("/api/me");
    commit({ ...session, me });
    return me;
  } catch { return session?.me || null; }
}

export async function setWatchlist(permits) {
  const me = await api("/api/watchlist", { method: "PUT", body: { permits: [...permits] } });
  commit({ ...session, me });
  return me;
}

export async function activateCheckout(sessionId) {
  const me = await api("/api/activate", { method: "POST", body: { session_id: sessionId } });
  commit({ ...session, me });
  return me;
}
export const sessionEmail = (sessionId) => api(`/api/session-email?session_id=${encodeURIComponent(sessionId)}`);
export const openPortal = () => api("/api/portal", { method: "POST" }).then((d) => { window.location.href = d.url; });
export const changePassword = (current, next) => api("/api/password", { method: "POST", body: { current, next } });

export const adminApi = {
  users: () => api("/api/admin/users"),
  update: (email, patch) => api("/api/admin/user", { method: "POST", body: { email, ...patch } }),
};

/* Stripe Payment Link with the buyer pre-identified so the webhook can attach
 * the subscription even if they never return to /welcome. client_reference_id
 * only allows [A-Za-z0-9_-], so the email travels base64url-encoded. */
export function checkoutUrl(email) {
  if (!PAYMENT_LINK) return "";
  if (!email) return PAYMENT_LINK;
  const ref = btoa(email).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const sep = PAYMENT_LINK.includes("?") ? "&" : "?";
  return `${PAYMENT_LINK}${sep}prefilled_email=${encodeURIComponent(email)}&client_reference_id=${ref}`;
}

/* ------------------------------------------------------------- hooks */
export function useAccount() {
  const [, tick] = useState(0);
  useEffect(() => {
    const upd = () => tick((n) => n + 1);
    window.addEventListener(EVT, upd);
    window.addEventListener("storage", upd);
    refreshMe();
    return () => { window.removeEventListener(EVT, upd); window.removeEventListener("storage", upd); };
  }, []);
  return { user: getAccount(), plan: getPlan(), signedIn: !!session?.token };
}
export function usePlan() { return useAccount().plan; }

/* ------------------------------------------------------------- CSV export */
export function downloadCsv(filename, header, rows) {
  const cell = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(cell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
